import { restClientV2 } from '@/api/client'
import { endpoints } from '@/api/endpoints'

const q = (p: Record<string, unknown>) => new URLSearchParams(
  Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== '').reduce((a, [k, v]) => ({ ...a, [k]: String(v) }), {})
).toString()

export interface ChatUser {
  id: number
  name?: string | null
  phone?: string | null
  photo_url?: string | null
  user_type?: string
}

export interface ChatMessage {
  id: number
  conversation_id: number
  sender_id?: number | null
  sender_type: 'user' | 'admin' | 'agent'
  message: string
  read_at?: string | null
  created_at: string
}

export interface ChatConversation {
  id: number
  subject?: string | null
  status: string
  priority?: string
  owner?: ChatUser | null
  assigned_to?: number | null
  assignee?: ChatUser | null
  last_message?: ChatMessage | null
  // The conversations list returns the latest message text directly on the row
  // (last_message is null there) — getMessages still returns full ChatMessage rows.
  message?: string | null
  unread: number
  updated_at: string
  created_at: string
}

export const chatService = {
  listConversations: (p: { status?: string; search?: string; page?: number; limit?: number } = {}) => {
    const params: Record<string, unknown> = { page: p.page ?? 1, limit: p.limit ?? 30 }
    if (p.status) params.status = p.status
    if (p.search) params.search = p.search
    return restClientV2.get(`${endpoints.chat.conversations}?${q(params)}`).then(r => r.data?.data as {
      total: number; count_per_page: number; page: number; conversations: ChatConversation[]
    })
  },
  startConversation: (data: { subject?: string; message: string }) =>
    restClientV2.post(endpoints.chat.conversations, data).then(r => r.data?.data),
  getMessages: (id: number, p: { page?: number; limit?: number } = {}) => {
    const params: Record<string, unknown> = { page: p.page ?? 1, limit: p.limit ?? 100 }
    return restClientV2.get(`${endpoints.chat.messages(id)}?${q(params)}`).then(r => r.data?.data as {
      conversation: { id: number; subject?: string | null; status: string; owner?: ChatUser | null; assigned_to?: number | null; assignee?: ChatUser | null }
      total: number; messages: ChatMessage[]
    })
  },
  sendMessage: (id: number, message: string) =>
    restClientV2.post(endpoints.chat.messages(id), { message }).then(r => r.data?.data as ChatMessage),
  markRead: (id: number) => restClientV2.post(endpoints.chat.read(id), {}).then(r => r.data?.data),
  unreadCount: () => restClientV2.get(endpoints.chat.unreadCount).then(r => r.data?.data as { unread: number }),

  // Admin starts a chat with a specific user.
  adminStart: (data: { user_id: number; subject?: string; message: string }) =>
    restClientV2.post(endpoints.chat.adminStart, data).then(r => r.data?.data),
  // Assign (agentId) or un-assign (null) a conversation.
  assign: (id: number, assignedTo: number | null) =>
    restClientV2.post(endpoints.chat.assign(id), { assigned_to: assignedTo }).then(r => r.data?.data),
  setStatus: (id: number, status: string) =>
    restClientV2.post(endpoints.chat.status(id), { status }).then(r => r.data?.data),
  listAgents: () => restClientV2.get(endpoints.chat.agents).then(r => r.data?.data as { agents: ChatUser[] }),
  searchUsers: (search: string) =>
    restClientV2.get(`${endpoints.chat.usersSearch}?${q({ search })}`).then(r => r.data?.data as { users: ChatUser[] }),
}
